// Utilities for robust scrolling behavior across browsers (incl. iOS Safari/Chrome)

/**
 * Ensures that a target element inside a scrollable container is visible.
 * Attempts native scrollIntoView first, then falls back to manual scroll using
 * bounding rects for reliability on mobile Safari/Chrome.
 *
 * @param {HTMLElement} containerEl - Scrollable container element
 * @param {HTMLElement|string|null} targetElOrSelector - Target element or selector string resolved within container
 * @param {Object} options - Optional settings
 * @param {boolean} [options.smooth=true] - Use smooth scroll where supported
 * @param {number} [options.padding=8] - Padding in pixels when adjusting manually
 * @param {number} [options.fallbackDelayMs=60] - Delay before running manual fallback
 * @param {boolean} [options.centerFallback=true] - Center approximately if no movement occurred
 * @returns {boolean} - True if a target element was found and an attempt to scroll was made
 */
export function ensureElementVisible(containerEl, targetElOrSelector, options = {}) {
    if (!containerEl) return false;

    const {
        smooth = true,
        padding = 8,
        fallbackDelayMs = 60,
        centerFallback = true,
    } = options;

    let targetEl = null;
    if (typeof targetElOrSelector === 'string') {
        try {
            targetEl = containerEl.querySelector(targetElOrSelector);
        } catch (_) {
            targetEl = null;
        }
    } else if (targetElOrSelector && typeof targetElOrSelector === 'object') {
        targetEl = targetElOrSelector;
    }

    if (!targetEl) return false;

    const beforeScrollTop = containerEl.scrollTop;

    // Try native scroll first
    try {
        if (typeof targetEl.scrollIntoView === 'function') {
            targetEl.scrollIntoView({
                behavior: smooth ? 'smooth' : 'auto',
                block: 'nearest',
                inline: 'nearest',
            });
        }
    } catch (_) {
        // ignore
    }

    // Manual nearest adjustment using bounding rects
    setTimeout(() => {
        try {
            const rootRect = containerEl.getBoundingClientRect();
            const elRect = targetEl.getBoundingClientRect();
            if (elRect.top < rootRect.top) {
                containerEl.scrollTop += elRect.top - rootRect.top - padding;
            } else if (elRect.bottom > rootRect.bottom) {
                containerEl.scrollTop += elRect.bottom - rootRect.bottom + padding;
            } else if (centerFallback && Math.abs(containerEl.scrollTop - beforeScrollTop) < 1) {
                // As a last resort, approximate centering
                const elementTop = targetEl.offsetTop;
                const elementHeight = targetEl.offsetHeight;
                const containerHeight = containerEl.clientHeight;
                const targetTop = Math.max(0, elementTop - Math.max(0, (containerHeight - elementHeight) / 2));
                try {
                    if (typeof containerEl.scrollTo === 'function') {
                        containerEl.scrollTo({ top: targetTop, behavior: 'auto' });
                    } else {
                        containerEl.scrollTop = targetTop;
                    }
                } catch (_) {
                    containerEl.scrollTop = targetTop;
                }
            }
        } catch (_) {
            // ignore
        }
    }, fallbackDelayMs);

    return true;
}


